/**
 * Agent A1 — Intake Elicitation (V85-4-1 / ADR-0034 / gap B11).
 *
 * Given partial AI-system metadata (name, description, known deployment
 * info), asks targeted questions to fill the v2026.09 vocabulary fields
 * and proposes classifications to the `proposed_changes` queue.
 *
 * Provenance: every proposed field includes a `quoted_span` citing the
 * input text that supports the suggestion, plus a confidence score.
 * The agent never proposes a jurisdiction the system hasn't been
 * deployed in — only jurisdictions explicitly mentioned in the input.
 *
 * Runs through the V85-4-0 agent harness (`runAgent`).
 */
import { defineTemplate, field } from '../egress/data-classes.js';
import { LlmRefusedError } from '../egress/gateway.js';
import { VOCABULARY, BOOLEAN_FIELDS, SCALAR_FIELDS, ARRAY_FIELDS, } from './jurisdiction-applicability-service.js';
// ---- Agent definition -------------------------------------------------------
export const A1_DEFINITION = {
    agent_id: 'A1',
    prompt_version: '2026.09.20',
    queue_target: 'proposed_changes',
    schema_version: '1.0',
};
/** Output schema for harness validation. */
export const A1_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['target_type', 'system_name', 'purpose', 'field_proposals'],
    properties: {
        target_type: { type: 'string' },
        system_name: { type: 'string' },
        purpose: { type: 'string' },
        deployment_jurisdictions: { type: 'array' },
        field_proposals: { type: 'array' },
    },
};
// ---- Known jurisdiction codes -----------------------------------------------
// Only jurisdictions the system explicitly mentions are allowed.
// This set is used to normalize free-text jurisdiction mentions to codes.
const JURISDICTION_KEYWORDS = {
    'new york': 'US-NY',
    'nyc': 'US-NY',
    'ny': 'US-NY',
    'colorado': 'US-CO',
    'denver': 'US-CO',
    'california': 'US-CA',
    'illinois': 'US-IL',
    'chicago': 'US-IL',
    'texas': 'US-TX',
    'florida': 'US-FL',
    'connecticut': 'US-CT',
    'virginia': 'US-VA',
    'maryland': 'US-MD',
    'washington': 'US-WA',
    'oregon': 'US-OR',
    'massachusetts': 'US-MA',
    'united states': 'US',
    'us': 'US',
    'usa': 'US',
    'eu': 'EU',
    'european union': 'EU',
    'europe': 'EU',
    'uk': 'UK',
    'united kingdom': 'UK',
    'canada': 'CA',
    'australia': 'AU',
    'japan': 'JP',
    'china': 'CN',
    'india': 'IN',
    'brazil': 'BR',
    'singapore': 'SG',
    'south korea': 'KR',
    'germany': 'EU-DE',
    'france': 'EU-FR',
    'italy': 'EU-IT',
    'spain': 'EU-ES',
    'netherlands': 'EU-NL',
};
// ---- Prompt -----------------------------------------------------------------
const INTAKE_PROMPT_TEMPLATE = `You are an AI governance intake analyst. Given a description of an AI system, extract structured classification fields.

## Vocabulary fields (v2026.09)

Boolean fields (true/false):
{boolean_fields}

Scalar fields (single value):
{scalar_fields}
- decision_role: "sole_decision" | "recommendation" | "assistance" | "none"
- human_oversight_mode: "hitl" | "hotl" | "none"
- autonomy_level: "full" | "partial" | "advisory" | "none"
- sector: e.g. "healthcare", "finance", "education", "hr", "legal", "government", "retail"
- deployment_target: "internal" | "external" | "both"
- gpai_role: "none" | "provider" | "deployer"
- taxonomy_type: e.g. "classification", "generation", "recommendation", "detection"

Array fields (lists):
{array_fields}
- data_classes: types of data (e.g. "resume", "medical_image", "biometric", "financial", "video_interview")
- data_categories: categories (e.g. "personal", "sensitive", "public")
- affected_parties: who is affected (e.g. "employees", "consumers", "patients", "students")
- deployment_jurisdictions: ISO-style jurisdiction codes

## Instructions

1. Read the system description carefully.
2. For each vocabulary field, determine if the description provides evidence for a value.
3. For each proposed field, quote the EXACT span from the description that supports your classification.
4. Only propose deployment_jurisdictions that are EXPLICITLY mentioned in the description. Never infer jurisdictions.
5. Return confidence 0.0-1.0 for each field based on how clearly the description supports it.

## System description

Name: {system_name}
Description: {description}
{documents_section}
{known_jurisdictions_section}

## Output format (JSON)

Return a JSON object with:
- system_name: string (cleaned/normalized system name)
- purpose: string (one-sentence purpose statement)
- deployment_jurisdictions: string[] (only explicitly mentioned jurisdictions)
- field_proposals: array of { field, value, confidence, quoted_span }
`;
// ---- Helpers ----------------------------------------------------------------
/** ADR-0073: the A1 prompt as a reviewed template; the description and documents are tenant data. */
export const A1_TEMPLATE = defineTemplate('agent.a1.intake', '2026.09.20', INTAKE_PROMPT_TEMPLATE, [
    'boolean_fields', 'scalar_fields', 'array_fields', 'system_name', 'description', 'documents_section', 'known_jurisdictions_section',
]);
function promptSlots(input) {
    const docsSection = input.documents?.length
        ? `\nSupporting documents:\n${input.documents.map((d, i) => `--- Document ${i + 1} ---\n${d}`).join('\n\n')}`
        : '';
    const jurisdictionsSection = input.deployment_jurisdictions?.length
        ? `\nKnown deployment jurisdictions: ${input.deployment_jurisdictions.join(', ')}`
        : '';
    return {
        boolean_fields: field('rules.vocabulary', BOOLEAN_FIELDS.join(', ')),
        scalar_fields: field('rules.vocabulary', SCALAR_FIELDS.join(', ')),
        array_fields: field('rules.vocabulary', ARRAY_FIELDS.join(', ')),
        system_name: field('ai_system.description', input.system_name ?? 'Not provided'),
        description: field('ai_system.description', input.description),
        documents_section: field('ai_system.documents', docsSection),
        known_jurisdictions_section: field('ai_system.description', jurisdictionsSection),
    };
}
/**
 * Extract jurisdiction codes from text. Only returns jurisdictions
 * that are explicitly mentioned — never infers.
 */
export function extractJurisdictions(text) {
    const lower = text.toLowerCase();
    const found = new Set();
    for (const [keyword, code] of Object.entries(JURISDICTION_KEYWORDS)) {
        // Word-boundary match to avoid false positives
        const pattern = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        if (pattern.test(lower)) {
            found.add(code);
        }
    }
    return [...found].sort();
}
/**
 * Validate that all proposed fields are in the vocabulary.
 */
export function validateFieldProposals(proposals) {
    const vocabSet = new Set(VOCABULARY);
    return proposals
        .filter(p => vocabSet.has(p.field))
        .map(p => ({
        field: p.field,
        value: p.value,
        confidence: Math.max(0, Math.min(1, p.confidence)),
        quoted_span: p.quoted_span,
    }));
}
/**
 * Filter jurisdiction proposals: only keep codes that appear in the input text.
 * This is the hard guard against fabricated jurisdictions.
 */
export function filterJurisdictions(proposed, inputText, knownJurisdictions) {
    const mentionedCodes = new Set(extractJurisdictions(inputText));
    // Also include any codes the caller passed explicitly
    if (knownJurisdictions) {
        for (const j of knownJurisdictions) {
            mentionedCodes.add(j.toUpperCase());
        }
    }
    // Only keep proposed jurisdictions that are in the mentioned set
    return proposed.filter(j => mentionedCodes.has(j));
}
// ---- Agent function ---------------------------------------------------------
/**
 * The A1 intake elicitation agent function. This is passed to `runAgent`.
 */
export const intakeElicitationAgent = async (llm, input) => {
    const data = input.data;
    if (!data.description || data.description.trim().length === 0) {
        throw new Error('A1 intake: description is required');
    }
    const fullText = [
        data.system_name ?? '',
        data.description,
        ...(data.documents ?? []),
    ].join(' ');
    // Structured extraction through the egress gateway (ADR-0073). A refusal
    // (no eligible provider for this workspace's data) surfaces as LlmRefusedError:
    // parity agent_a1_intake → the person fills in the intake form.
    const result = await llm.run({
        task: 'agent.a1.intake', template: A1_TEMPLATE, slots: promptSlots(data),
        schemaHint: A1_OUTPUT_SCHEMA, options: { temperature: 0.1, maxTokens: 4096 },
    });
    if (result.status !== 'ok')
        throw new LlmRefusedError(result);
    const rawOutput = result.output;
    // Validate field proposals against vocabulary
    const validatedProposals = validateFieldProposals(rawOutput.field_proposals ?? []);
    // Filter jurisdictions: only keep those explicitly mentioned in input
    const filteredJurisdictions = filterJurisdictions(rawOutput.deployment_jurisdictions ?? [], fullText, data.deployment_jurisdictions);
    // Build provenance map
    const provenance = {
        system_name: {
            source: 'input.description',
            span: data.system_name ?? data.description.slice(0, 100),
            confidence: rawOutput.system_name ? 0.9 : 0.5,
        },
        purpose: {
            source: 'input.description',
            span: data.description.slice(0, 200),
            confidence: 0.8,
        },
        deployment_jurisdictions: {
            source: 'input.description',
            span: filteredJurisdictions.join(', '),
            confidence: 0.95,
        },
        field_proposals: {
            source: 'input.description',
            span: `${validatedProposals.length} fields extracted`,
            confidence: validatedProposals.length > 0
                ? validatedProposals.reduce((sum, p) => sum + p.confidence, 0) / validatedProposals.length
                : 0,
        },
    };
    // Add per-field provenance
    for (const proposal of validatedProposals) {
        provenance[`field.${proposal.field}`] = {
            source: 'input.description',
            span: proposal.quoted_span,
            confidence: proposal.confidence,
        };
    }
    return {
        target: 'proposed_changes',
        proposal: {
            target_type: 'ai_systems',
            system_name: rawOutput.system_name ?? data.system_name ?? 'Unknown System',
            purpose: rawOutput.purpose ?? '',
            deployment_jurisdictions: filteredJurisdictions,
            field_proposals: validatedProposals,
        },
        provenance,
    };
};
// ---- Legacy API (kept for backward compat) ----------------------------------
/**
 * @deprecated Use `intakeElicitationAgent` with `runAgent` instead.
 * Analyse a free-text system description to propose structured intake fields.
 * Stub retained for backward compatibility — returns empty array.
 */
export async function elicitIntakeFields(_description, _documents) {
    return [];
}
//# sourceMappingURL=agent-intake-elicitation-service.js.map